import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Switch,
  FormControlLabel,
  SelectChangeEvent,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { merchantsService, dictionariesService } from '../../../services';
import { Merchant, PaymentSystem, MerchantRate } from '../../../types/index';
import AnimatedPage from '../../../components/AnimatedPage';
import { useMerchantRates } from '../hooks/useMerchantRates';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { canPerformAction, ActionKey } from '../../../auth/actionMatrix';
import { useNotification } from '../../../contexts/NotificationContext';

interface MerchantRateFormData {
  merchant_id: string;
  payment_system_id: string;
  rate: number;
  direction: string;
  additional_commission: number;
  is_active: boolean;
  is_test_mode: boolean;
  id: string;
  priority: number;
}

interface MerchantRatesRef {
  handleOpenDialog: () => void;
}

const MerchantRates = forwardRef<MerchantRatesRef>((props, ref) => {
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [paymentSystems, setPaymentSystems] = useState<PaymentSystem[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState<MerchantRateFormData>({
    merchant_id: '',
    payment_system_id: '',
    rate: 0,
    direction: '',
    additional_commission: 0,
    is_active: true,
    is_test_mode: false,
    id: '',
    priority: 0,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRate, setSelectedRate] = useState<MerchantRate | null>(null);
  const [search, setSearch] = useState('');
  const [directionFilter, setDirectionFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');

  const { rates, loading, error, refetch, patchRate } = useMerchantRates();
  const filteredRates = useMemo(() => { const needle = search.trim().toLowerCase(); return rates.filter((rate) => (!needle || rate.merchant_name?.toLowerCase().includes(needle) || rate.payment_system_name?.toLowerCase().includes(needle) || rate.id.toLowerCase().includes(needle)) && (!directionFilter || rate.direction.toLowerCase() === directionFilter) && (!activeFilter || rate.is_active === (activeFilter === 'active'))); }, [rates, search, directionFilter, activeFilter]);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const { showNotification } = useNotification();

  const canCreate = useMemo(
    () => canPerformAction(currentUser, ActionKey.MERCHANT_RATES_CREATE),
    [currentUser],
  );
  const canUpdate = useMemo(
    () => canPerformAction(currentUser, ActionKey.MERCHANT_RATES_UPDATE),
    [currentUser],
  );
  const canDelete = useMemo(
    () => canPerformAction(currentUser, ActionKey.MERCHANT_RATES_DELETE),
    [currentUser],
  );
  const canToggleActive = useMemo(
    () => canPerformAction(currentUser, ActionKey.MERCHANT_RATES_TOGGLE_ACTIVE),
    [currentUser],
  );

  useImperativeHandle(ref, () => ({
    handleOpenDialog: () => {
      if (!canCreate) {
        showNotification('У вас нет прав для создания ставок мерчантов', 'error');
        return;
      }
      handleOpenDialog();
    }
  }));

  const fetchMerchants = async () => {
    try {
      const merchantsData = await merchantsService.getMerchants();
      setMerchants(merchantsData);
    } catch (err: any) {
      console.error('Error fetching merchants:', err);
    }
  };

  const fetchPaymentSystems = async () => {
    try {
      const systemsData = await dictionariesService.getPaymentSystems();
      setPaymentSystems(systemsData);
    } catch (err: any) {
      console.error('Error fetching payment systems:', err);
    }
  };

  useEffect(() => {
    fetchMerchants();
    fetchPaymentSystems();
  }, []);

  const handleOpenDialog = (rate?: MerchantRate) => {
    if (rate) {
      setSelectedRate(rate);
      setFormData({
        merchant_id: rate.merchant_id,
        payment_system_id: rate.payment_system_id,
        rate: rate.rate,
        direction: rate.direction,
        additional_commission: rate.additional_commission,
        is_active: rate.is_active,
        is_test_mode: rate.is_test_mode,
        id: rate.id,
        priority: rate.priority,
      });
    } else {
      setSelectedRate(null);
      setFormData({
        merchant_id: '',
        payment_system_id: '',
        rate: 0,
        direction: '',
        additional_commission: 0,
        is_active: true,
        is_test_mode: false,
        id: '',
        priority: 0,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRate(null);
    setFormData({
      merchant_id: '',
      payment_system_id: '',
      rate: 0,
      direction: '',
      additional_commission: 0,
      is_active: true,
      is_test_mode: false,
      id: '',
      priority: 0,
    });
  };

  const handleChange = (field: keyof MerchantRateFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.type === 'checkbox' ? e.target.checked : parseFloat(e.target.value) || 0,
    }));
  };

  const handleSelectChange = (field: 'merchant_id' | 'payment_system_id' | 'direction') => (e: SelectChangeEvent) => {
    setFormData((prev) => ({
      ...prev,
      [field]: field === 'direction' ? e.target.value.toUpperCase() : e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (selectedRate) {
        if (!canUpdate) {
          showNotification('У вас нет прав для редактирования ставок мерчантов', 'error');
          return;
        }
        await merchantsService.updateMerchantRate(selectedRate.id, formData);
      } else {
        if (!canCreate) {
          showNotification('У вас нет прав для создания ставок мерчантов', 'error');
          return;
        }
        const { id, ...createData } = formData;
        await merchantsService.createMerchantRate(createData);
      }
      refetch();
      handleCloseDialog();
    } catch (err: any) {
      setFormError(err.message || 'Ошибка при сохранении ставки');
    }
  };

  const handleDelete = async () => {
    if (!selectedRate) return;
    try {
      if (!canDelete) {
        showNotification('У вас нет прав для удаления ставок мерчантов', 'error');
        return;
      }
      await merchantsService.deleteMerchantRate(selectedRate.id);
      refetch();
      setDeleteDialogOpen(false);
      setSelectedRate(null);
    } catch (err: any) {
      console.error('Error deleting rate:', err);
    }
  };

  const handleToggleActive = async (rate: MerchantRate) => {
    try {
      if (!canToggleActive) {
        showNotification('У вас нет прав для изменения статуса ставок мерчантов', 'error');
        return;
      }
      const updatedRate = await merchantsService.toggleMerchantRateActive(rate.id);
      patchRate(rate.id, {
        is_active: updatedRate?.is_active ?? !rate.is_active,
        updated_at: updatedRate?.updated_at ?? rate.updated_at,
      });
      showNotification(
        `Ставка мерчанта успешно ${updatedRate?.is_active ? 'активирована' : 'деактивирована'}`,
        'success'
      );
    } catch (err: any) {
      console.error('Error toggling rate:', err);
      showNotification('Ошибка при обновлении статуса ставки мерчанта', 'error');
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
        Ставки мерчантов
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}><Stack direction={{ xs: 'column', md: 'row' }} spacing={2}><TextField fullWidth label="Поиск по мерчанту, методу или ID" value={search} onChange={(event) => setSearch(event.target.value)} /><FormControl sx={{ minWidth: 180 }}><InputLabel>Направление</InputLabel><Select value={directionFilter} label="Направление" onChange={(event) => setDirectionFilter(event.target.value)}><MenuItem value="">Все</MenuItem><MenuItem value="in">IN</MenuItem><MenuItem value="out">OUT</MenuItem></Select></FormControl><FormControl sx={{ minWidth: 180 }}><InputLabel>Активность</InputLabel><Select value={activeFilter} label="Активность" onChange={(event) => setActiveFilter(event.target.value)}><MenuItem value="">Все</MenuItem><MenuItem value="active">Активные</MenuItem><MenuItem value="inactive">Неактивные</MenuItem></Select></FormControl><Button onClick={() => { setSearch(''); setDirectionFilter(''); setActiveFilter(''); }}>Сбросить</Button></Stack></Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Мерчант</TableCell>
                <TableCell>Платежная система</TableCell>
                <TableCell>Ставка</TableCell>
                <TableCell>Направление</TableCell>
                <TableCell>Доп. комиссия</TableCell>
                <TableCell>Активна</TableCell>
                <TableCell>Режим</TableCell>
                <TableCell>Обновлено</TableCell>
                <TableCell>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRates.map((rate) => (
                <TableRow key={rate.id}>
                  <TableCell>{rate.merchant_name}</TableCell>
                  <TableCell>{rate.payment_system_name}</TableCell>
                  <TableCell>{rate.rate}</TableCell>
                  <TableCell>{rate.direction}</TableCell>
                  <TableCell>{rate.additional_commission}</TableCell>
                <TableCell>
                  <Tooltip title={canToggleActive ? (rate.is_active ? 'Деактивировать' : 'Активировать') : 'Недостаточно прав'}>
                    <span>
                      <Switch
                        checked={rate.is_active}
                        onChange={() => handleToggleActive(rate)}
                        color="primary"
                        disabled={!canToggleActive}
                      />
                    </span>
                  </Tooltip>
                </TableCell>
                  <TableCell>{rate.is_test_mode ? 'Sandbox' : 'Боевой'}</TableCell>
                  <TableCell>
                    {new Date(rate.updated_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title={canUpdate ? 'Редактировать' : 'Недостаточно прав'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (!canUpdate) {
                                showNotification('У вас нет прав для редактирования ставок мерчантов', 'error');
                                return;
                              }
                              handleOpenDialog(rate);
                            }}
                            disabled={!canUpdate}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={canDelete ? 'Удалить' : 'Недостаточно прав'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (!canDelete) {
                                showNotification('У вас нет прав для удаления ставок мерчантов', 'error');
                                return;
                              }
                              setSelectedRate(rate);
                              setDeleteDialogOpen(true);
                            }}
                            color="error"
                            disabled={!canDelete}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        disableEnforceFocus
        disableAutoFocus
      >
        <DialogTitle>
          {selectedRate ? 'Редактировать ставку' : 'Добавить ставку'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Мерчант</InputLabel>
              <Select
                value={formData.merchant_id}
                onChange={handleSelectChange('merchant_id')}
                label="Мерчант"
                required
              >
                {merchants.map((merchant) => (
                  <MenuItem key={merchant.id} value={merchant.id}>
                    {merchant.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Платежная система</InputLabel>
              <Select
                value={formData.payment_system_id}
                onChange={handleSelectChange('payment_system_id')}
                label="Платежная система"
                required
              >
                {paymentSystems.map((system) => (
                  <MenuItem key={system.id} value={system.id}>
                    {system.token}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Направление</InputLabel>
              <Select
                value={formData.direction?.toLowerCase()}
                onChange={handleSelectChange('direction')}
                label="Направление"
                required
              >
                <MenuItem value="in">Входящие</MenuItem>
                <MenuItem value="out">Исходящие</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Ставка"
              type="number"
              value={formData.rate}
              onChange={handleChange('rate')}
              margin="normal"
              required
              inputProps={{ step: "0.01" }}
            />

            <TextField
              fullWidth
              label="Дополнительная комиссия"
              type="number"
              value={formData.additional_commission}
              onChange={handleChange('additional_commission')}
              margin="normal"
              required
              inputProps={{ step: "0.01" }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={handleChange('is_active')}
                  color="primary"
                />
              }
              label="Активна"
              sx={{ mt: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_test_mode}
                  onChange={handleChange('is_test_mode')}
                  color="warning"
                />
              }
              label="Тестовый режим (Sandbox)"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedRate ? 'Сохранить' : 'Добавить'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        disableEnforceFocus
        disableAutoFocus
      >
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите удалить ставку для мерчанта {selectedRate?.merchant_name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedRate(null);
            }}
          >
            Отмена
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default MerchantRates;
